/**
 * AppBroadcast - Shared Topic Registry for Pub/Sub Routing
 *
 * Single source of truth for all PubSub event topics across the StreamSyncEngage platform.
 * Used by both client-saas and client-sse-console applications.
 *
 * Hierarchical topic structure for event-driven architecture:
 * - APP.* - Application-level events
 * - ROUTE.* - Navigation events
 * - AUTH.* - Authentication events
 * - UI.* - User interface events
 * - I18N.* - Internationalization events
 * - DATA.* - Data events
 * - M5T_PRIMARY_NAV.* - Primary navigation events
 *
 * Usage:
 *   import AppBroadcast from '../../../../shared/appBroadcast.js';
 *   PubSub.subscribe(AppBroadcast.AUTH_SIGNIN_SUCCESS, handler);
 *   PubSub.publish(AppBroadcast.I18N_SET_LANG, { langCode: 'es' });
 */

const AppBroadcast = Object.freeze({
  // Application Events
  APP: 'APP',
  APP_READY: 'APP.READY',
  APP_ERROR: 'APP.ERROR',
  APP_SIGNOUT: 'APP.SIGNOUT',
  APP_AUTH: 'APP.AUTH',
  APP_USER_LOADED: 'APP.USER.LOADED',

  // Route Navigation Events
  // NOTE: pubsub-js delivers topics hierarchically (publishing 'A.B.C' also notifies
  // subscribers of 'A.B' and 'A'). "Command" topics whose subscribers navigate must NOT be a
  // dotted parent of an event topic that is published on its own, or the event bubbles up and
  // re-triggers the command (a navigation storm). So the command is a `.GO` sibling of its
  // events, never their parent. Do NOT collapse these back to a bare 'ROUTE.NAV'/'AUTH.SIGNUP'.
  ROUTE_NAV: 'ROUTE.NAV.GO',
  ROUTE_NAV_BEFORE: 'ROUTE.NAV.BEFORE',
  ROUTE_NAV_AFTER: 'ROUTE.NAV.AFTER',
  ROUTE_NAV_ERROR: 'ROUTE.NAV.ERROR',

  // Authentication Events
  AUTH_SIGNIN: 'AUTH.SIGNIN.GO',
  AUTH_SIGNIN_SUCCESS: 'AUTH.SIGNIN.SUCCESS',
  AUTH_SIGNIN_FAIL: 'AUTH.SIGNIN.FAIL',
  AUTH_SIGNUP: 'AUTH.SIGNUP.GO',
  AUTH_SIGNUP_SUCCESS: 'AUTH.SIGNUP.SUCCESS',
  AUTH_SIGNUP_FAIL: 'AUTH.SIGNUP.FAIL',
  AUTH_VERIFY_LINK: 'AUTH.VERIFY.LINK',
  AUTH_SIGNOUT: 'AUTH.SIGNOUT.GO',
  AUTH_SIGNOUT_SUCCESS: 'AUTH.SIGNOUT.SUCCESS',
  AUTH_FORGOT: 'AUTH.FORGOT.GO',
  AUTH_FORGOT_SUCCESS: 'AUTH.FORGOT.SUCCESS',
  AUTH_RESETHASH: 'AUTH.RESETHASH.GO',
  AUTH_RESETHASH_SUCCESS: 'AUTH.RESETHASH.SUCCESS',
  AUTH_RESETHASH_NAV: 'AUTH.RESETHASH.NAV',
  AUTH_TOKEN_EXPIRED: 'AUTH.TOKEN.EXPIRED',
  AUTH_TOKEN_INVALID: 'AUTH.TOKEN.INVALID',
  AUTH_SCOPE_LOADED: 'AUTH.SCOPE.LOADED',     // tenancy scope (/me) resolved + cached

  // Primary and Secondary Nav
  M5T_PRIMARY_NAV: 'M5T_PRIMARY_NAV',
  M5T_PRIMARY_NAV_LOGO_TAP: 'M5T_PRIMARY_NAV.LOGO_TAP',
  M5T_PRIMARY_NAV_HOVER: 'M5T_PRIMARY_NAV.HOVER',
  M5T_PRIMARY_NAV_TAP: 'M5T_PRIMARY_NAV.TAP',
  M5T_PRIMARY_NAV_DOUBLE_TAP: 'M5T_PRIMARY_NAV.DOUBLE_TAP',
  M5T_PRIMARY_NAV_LONG_TAP: 'M5T_PRIMARY_NAV.LONG_TAP',

  // Side Nav (client-saas persona-aware left navigation)
  M5T_SIDE_NAV: 'M5T_SIDE_NAV',
  M5T_SIDE_NAV_TAP: 'M5T_SIDE_NAV.TAP',
  M5T_SIDE_NAV_COLLAPSE: 'M5T_SIDE_NAV.COLLAPSE',
  M5T_SIDE_NAV_LOGO_TAP: 'M5T_SIDE_NAV.LOGO_TAP',
  M5T_SIDE_NAV_ORG_SWITCH: 'M5T_SIDE_NAV.ORG_SWITCH',
  M5T_SIDE_NAV_FOOTER_ACTION: 'M5T_SIDE_NAV.FOOTER_ACTION',

  // UI Events
  UI_LOADING_START: 'UI.LOADING.START',
  UI_LOADING_END: 'UI.LOADING.END',
  UI_TOAST: 'UI.TOAST',
  UI_MODAL_OPEN: 'UI.MODAL.OPEN',
  UI_MODAL_CLOSE: 'UI.MODAL.CLOSE',
  UI_THEME_CHANGED: 'UI.THEME.CHANGED',
  UI_WELCOME_BACK_CLICKED: 'UI.WELCOME_BACK_CLICKED',

  // Data Events
  DATA_REFRESH: 'DATA.REFRESH',
  DATA_UPDATE: 'DATA.UPDATE',
  DATA_ERROR: 'DATA.ERROR',

  // I18N Events
  I18N: 'I18N',
  I18N_SET_LANG: 'I18N.SET_LANG',

  // Conversation Events
  CONVERSATION: 'CONVERSATION',
  CONVERSATION_PROMPT: 'CONVERSATION.PROMPT', // Create JS Switch in app_events at this level.
    CONVERSATION_PROMPT_AGENT_CANVAS: 'CONVERSATION.PROMPT.AGENT_CANVAS',  //  Send this prompt to the agent and canvas
    CONVERSATION_PROMPT_AGENT: 'CONVERSATION.PROMPT.AGENT', // Send this Prompt to the Agent only
    CONVERSATION_PROMPT_CANVAS: 'CONVERSATION.PROMPT.CANVAS', // Send this Prompt to the Canvas only
    CONVERSATION_PROMPT_STAGE: 'CONVERSATION.PROMPT.STAGE', // Render this Prompt in the Prompt Area so the user can change then click send

  CONVERSATION_RESPONSE: 'CONVERSATION.RESPONSE', // Create JS Switch in app_events at this level.
  CONVERSATION_RESPONSE_CANVAS: 'CONVERSATION.RESPONSE.CANVAS', // Stream this Reponse to the Canvas
  CONVERSATION_RESPONSE_WORKFLOW: 'CONVERSATION.RESPONSE.WORKFLOW',  //  Stream this Response to the Workflow Engine (tm)

  // Conversation Events
  CANVAS: 'CANVAS',
    CANVAS_DO_SCROLL: 'CANVAS.DO.SCROLL', //

  // Message Events (sseEtherMessage)
  MESSAGE: 'MESSAGE',
    MESSAGE_CREATE: 'MESSAGE.CREATE', // Create new message
    MESSAGE_CREATED: 'MESSAGE.CREATED', // Message created successfully (payload: {messageId})
    MESSAGE_EDIT: 'MESSAGE.EDIT', // Edit existing message (payload: {messageId})
    MESSAGE_UPDATED: 'MESSAGE.UPDATED', // Message updated successfully (payload: {messageId})
    MESSAGE_DELETE: 'MESSAGE.DELETE', // Delete message (payload: {messageId})
    MESSAGE_DELETED: 'MESSAGE.DELETED', // Message deleted successfully (payload: {messageId})
    MESSAGE_REFRESH: 'MESSAGE.REFRESH', // Reload messages

});

export default AppBroadcast;
